import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ClientFeedback } from '../../../team-apis/otterdam/feedback';
import { Task } from '../../../team-apis/otterdam/task';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import {
  clientFeedbackTagsPath,
  getClientFeedbackPath,
} from '../../shared/resources/client-issue-paths';
import { ClientFeedbackTag } from '../../shared/resources/client-issue-resource.service';
import { AbstractResource } from '../abstract-resource';

@Injectable()
export class ClientFeedbackLoadingService extends AbstractResource {
  private store = inject(TaskWorkspaceStore);

  async load(task: Task) {
    const { projectId } = this.workspaceConfig;

    await Promise.all([
      this.loadFeedback(projectId, task.id),
      this.loadTags(projectId),
    ]);
  }

  private async loadFeedback(projectId: number, taskId: string) {
    const issues$ = this.request$<{ data: ClientFeedback[] }>(
      'GET',
      getClientFeedbackPath(projectId, taskId),
      {
        tokenScope: 'access_token',
        skipDefaultErrorMessaging: true,
      },
    );
    try {
      const response = await firstValueFrom(issues$);
      this.store('clientFeedback').set(response.data);
    } catch (error: unknown) {
      this.handleError(error);
    }
  }

  private async loadTags(projectId: number) {
    const tags$ = this.request$<{ data: ClientFeedbackTag[] }>(
      'GET',
      clientFeedbackTagsPath(projectId),
      {
        tokenScope: 'access_token',
        skipDefaultErrorMessaging: true,
      },
    );
    try {
      const response = await firstValueFrom(tags$);
      const tags = response.data.map(({ tag }) => tag);
      this.store('clientFeedbackTags').set(tags);
    } catch (error: unknown) {
      this.handleError(error);
    }
  }

  private handleError(error: unknown) {
    if (error instanceof HttpErrorResponse) {
      // 403 errors can be expected for those entities
      if (error.status !== 403) {
        this.messagingService.error('Failed to load client feedback!');
      }
    }
  }
}
